import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';
import { RevisionPage } from './pages/RevisionPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="3684hdq2uasw">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="3gle9ehqxh1en">
      <BrowserRouter data-id="element-2" data-mp="3gkuxpirvf992">
        <ScrollToTop data-id="element-3" data-mp="3gm647qcpav3v" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-4" data-mp="3glnz048296jt">
          <SkipLink data-id="element-5" data-mp="3bo4l55f9yvse" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-6" data-mp="2xuwt5cna7ya6" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-7" data-mp="2sw1ztgrourbt">
            <Header data-id="element-8" data-mp="2svagb4bd8k1w" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-9" data-mp="18drizuto1dda">
            <Routes data-id="element-10" data-mp="1d4rin7u669jh">
              <Route path="/" element={<Home data-id="element-12" data-mp="1d6ko4jt1qnpw" />} data-id="element-11" data-mp="1d62jaysbj16d" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-14" data-mp="ygwl9e57vh02" />} data-id="element-13" data-mp="yfvbdczxt0jx" />
              <Route path="/pricing" element={<PricingPage data-id="element-16" data-mp="13e55rldu7x4h" />} data-id="element-15" data-mp="ydil8ck0zjvs" />
              <Route path="/faq" element={<FAQPage data-id="element-18" data-mp="13clphus6lm2p" />} data-id="element-17" data-mp="13fgapoj0rax6" />
              <Route path="/contact" element={<Contact data-id="element-20" data-mp="1r5ame2rlqtfu" />} data-id="element-19" data-mp="1r4ioumeg2lmx" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-22" data-mp="1w33qmq9chddh" />} data-id="element-21" data-mp="1r525lzscz3zd" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-24" data-mp="1i441lhhx6wgs" />} data-id="element-23" data-mp="1w210wlnrdgz5" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-26" data-mp="1m4weckq03jht" />} data-id="element-25" data-mp="1i5ni997hn9j3" />
              <Route path="/gracias" element={<GraciasPage data-id="element-28" data-mp="1m70ovliengr2" />} data-id="element-27" data-mp="1m3v24cx90qyi" />
              <Route path="/guia" element={<GuiaPage data-id="element-30" data-mp="6nmfobby8c8e" />} data-id="element-29" data-mp="6nw59xt30hdk" />
              <Route path="/pedido" element={<PedidoPage data-id="element-32" data-mp="apr8j7ukp4tz" />} data-id="element-31" data-mp="6qif643g38yy" />
              <Route path="/foto" element={<FotoPage data-id="element-34" data-mp="arc2owcz6lrb" />} data-id="element-33" data-mp="ar2d39vuegm5" />
              <Route path="/revision" element={<RevisionPage data-id="element-36" data-mp="qy7493gpb59m" />} data-id="element-35" data-mp="qx5jg79vv4ep" />
            </Routes>
          </main>
          <Footer data-id="element-37" data-mp="m6p42hmi58pp" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-38" data-mp="m4m352jt9v67" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}